import { info } from '@ms/yammer-console-logging';
import { loadProtobufClientProperties } from '@ms/yammer-telemetry';
import { getTelemetryHostingConfig } from '@ms/yammer-telemetry-store';
import { v4 as uuidv4 } from 'uuid';

import { getEtagFromStateOrPersistedState } from '../../domains/ecs/selectors';
import { getClientForwardExperiments, getExperimentTreatment } from '../../domains/experiments/selectors';
import { isFeatureRolloutEnabled } from '../../domains/featureRollouts/selectors';
import { getGraphqlHost } from '../../domains/session/selectors';
import type { YammerState } from '../../state/types';
import { addTreatmentsToRequest, getTreatmentsExtensionForGraphqlBody } from '../graphqlApiRequest';
import type { ApiHeaders, ApiMiddleware, GraphqlApiRequest, GraphqlOperation, ManagedApiRequest } from '../types';

import {
  GraphqlApiV5EndpointVersionNumber,
  isBackendGraphqlApiV5EndpointEnabledForOperation,
} from './graphqlApiV5Endpoint';

const getRequestHostOrDefault = ({ host }: ManagedApiRequest, state: YammerState) => host || getGraphqlHost(state);

const addCommonTreatmentsToRequestBody = (request: GraphqlApiRequest, state: YammerState): GraphqlApiRequest => {
  const treatments = getTreatmentsExtensionForGraphqlBody({
    state,
    featureRollouts: ['GraphqlForcedTracing', 'GroupArchPreview'],
    experiments: [
      ...getClientForwardExperiments(state),
      'DomainMigration',
      'DisablePollPostType',
      'StorylineMediaPostGA',
      'PrivateReplies',
    ],
  });

  return addTreatmentsToRequest({ request, treatments });
};

const getConditionalForcedTracingHeaders = (request: ManagedApiRequest, state: YammerState): ApiHeaders | undefined => {
  const { body } = request;
  const xRequestId = uuidv4();
  const headersWithXRequestId = {
    'X-Request-Id': xRequestId,
  };

  if (
    getTelemetryHostingConfig()?.hostingEnvironment !== 'dev' &&
    !isFeatureRolloutEnabled(state, 'GraphqlForcedTracing')
  ) {
    return headersWithXRequestId;
  }

  logForcedTraceHeaders(body as GraphqlOperation, xRequestId);

  return {
    ...headersWithXRequestId,
    // Header for forced tracing: https://github.com/openzipkin/b3-propagation#debug-flag-1
    'X-B3-Flags': '1',
  };
};

const getClientPropertiesHeaders = async (): Promise<ApiHeaders> => {
  const { getProtobufClientProperties } = await loadProtobufClientProperties();
  const clientProperties = await getProtobufClientProperties();

  return clientProperties ? { 'X-Analytics-Client-Properties': clientProperties } : {};
};

const getETagHeaders = async (state: YammerState): Promise<ApiHeaders | undefined> => {
  const etag = await getEtagFromStateOrPersistedState(state);

  return etag ? { 'X-ECS-ETag': etag } : undefined;
};

const logForcedTraceHeaders = ({ operationName, variables }: GraphqlOperation, xRequestId: string) => {
  info(`GraphQL ${operationName} ${xRequestId}
    Variables: ${JSON.stringify(variables) || '{}'}

    s50 App Insights: https://ms.portal.azure.com/#blade/AppInsightsExtension/BladeRedirect/BladeName/searchV1/ResourceId/%252Fsubscriptions%252F6d06938a-9469-47da-a813-ff39b4414c30%252FresourceGroups%252Fappinsights-rg%252Fproviders%252FMicrosoft.Insights%252Fcomponents%252Fs50stmpmngmntaiwus0/BladeInputs/%7B%22tables%22%3A%5B%22availabilityResults%22%2C%22requests%22%2C%22exceptions%22%2C%22pageViews%22%2C%22traces%22%2C%22customEvents%22%2C%22dependencies%22%5D%2C%22timeContextWhereClause%22%3A%22%7C%20where%20timestamp%20%3E%20datetime(%5C%222025-05-05T00%3A51%3A53.908Z%5C%22)%20and%20timestamp%20%3C%20datetime(%5C%222025-05-06T00%3A51%3A53.908Z%5C%22)%22%2C%22filterWhereClause%22%3A%22%7C%20where%20*%20has%20%5C%22${xRequestId}%5C%22%7C%20order%20by%20timestamp%20desc%22%2C%22originalParams%22%3A%7B%22eventTypes%22%3A%5B%7B%22value%22%3A%22availabilityResult%22%2C%22tableName%22%3A%22availabilityResults%22%2C%22label%22%3A%22Availability%22%7D%2C%7B%22value%22%3A%22request%22%2C%22tableName%22%3A%22requests%22%2C%22label%22%3A%22Request%22%7D%2C%7B%22value%22%3A%22exception%22%2C%22tableName%22%3A%22exceptions%22%2C%22label%22%3A%22Exception%22%7D%2C%7B%22value%22%3A%22pageView%22%2C%22tableName%22%3A%22pageViews%22%2C%22label%22%3A%22Page%20View%22%7D%2C%7B%22value%22%3A%22trace%22%2C%22tableName%22%3A%22traces%22%2C%22label%22%3A%22Trace%22%7D%2C%7B%22value%22%3A%22customEvent%22%2C%22tableName%22%3A%22customEvents%22%2C%22label%22%3A%22Custom%20Event%22%7D%2C%7B%22value%22%3A%22dependency%22%2C%22tableName%22%3A%22dependencies%22%2C%22label%22%3A%22Dependency%22%7D%5D%2C%22timeContext%22%3A%7B%22durationMs%22%3A86400000%7D%2C%22filter%22%3A%5B%5D%2C%22searchPhrase%22%3A%7B%22originalPhrase%22%3A%22${xRequestId}%22%2C%22_tokens%22%3A%5B%7B%22conjunction%22%3A%22and%22%2C%22value%22%3A%22${xRequestId}%22%2C%22isNot%22%3Afalse%2C%22kql%22%3A%22%20*%20has%20%5C%22${xRequestId}%5C%22%22%7D%5D%7D%2C%22sort%22%3A%22desc%22%7D%7D`);
};

const getOperationNameParameters = (request: ManagedApiRequest) => ({
  operationName: (request.body as GraphqlOperation).operationName,
});

const getApiVnextEndpoint = (state: YammerState, request: ManagedApiRequest): number => {
  const treatment = getExperimentTreatment(state, 'BackendGraphqlApiVnextEndpoint');
  const isBackendGraphqlApiV5EndpointEnabled = isBackendGraphqlApiV5EndpointEnabledForOperation(
    state,
    (request.body as GraphqlOperation).operationName
  );

  const value = isBackendGraphqlApiV5EndpointEnabled ? GraphqlApiV5EndpointVersionNumber : Number(treatment);
  if (isNaN(value) || !isFinite(value)) {
    return 0;
  }

  return value;
};

const isApiVnextEndpointEnabled = (value: number) => value !== 0;

const getGraphqlApiVnextParameters = (state: YammerState, request: ManagedApiRequest) => {
  const apiVnextEndpoint = getApiVnextEndpoint(state, request);

  return isApiVnextEndpointEnabled(apiVnextEndpoint) ? { apiVnext: apiVnextEndpoint } : {};
};

const middleware: ApiMiddleware = async (request, state) => {
  const updatedRequest = {
    ...request,
    host: getRequestHostOrDefault(request, state),
    headers: {
      ...request.headers,
      ...getConditionalForcedTracingHeaders(request, state),
      ...(await getClientPropertiesHeaders()),
      ...(await getETagHeaders(state)),
    },
    query: {
      ...getOperationNameParameters(request),
      ...getGraphqlApiVnextParameters(state, request),
    },
  };

  return addCommonTreatmentsToRequestBody(updatedRequest as GraphqlApiRequest, state);
};

export default middleware;
